import { Box, Button, DialogActions, useTheme } from "@mui/material";
import { Formik } from "formik";
import type { z } from "zod";

import { toFormikValidationSchema } from "@thalamos/common";
import type { FormBuilderField, TranslationKey } from "@thalamos/vision-common";
import { tk } from "@thalamos/vision-common";

import { TypographyI18N } from "../../index.js";
import { Field } from "../Field.js";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SinglePageForm = <Schema extends z.ZodType<any>>({
  schema,
  initialValues,
  onSubmit,
  content,
  canCancel,
  onCancel,
  confirmButtonLabel,
  canDisableSubmitButton = false,
  disableSubmitButton = false,
  cancelButtonLabel,
  ariaLabel,
}: {
  schema: Schema;
  initialValues: z.infer<Schema>;
  onSubmit: (values: z.infer<Schema>) => Promise<void> | void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: { fields: FormBuilderField<z.infer<Schema>, any>[] };
  canCancel?: boolean;
  onCancel?: () => void;
  confirmButtonLabel?: TranslationKey;
  canDisableSubmitButton?: boolean;
  disableSubmitButton?: boolean;
  cancelButtonLabel?: TranslationKey;
  ariaLabel: string;
}) => {
  const theme = useTheme();
  return (
    <Formik
      validationSchema={toFormikValidationSchema(schema)}
      validateOnChange={true}
      initialValues={initialValues}
      onSubmit={async (values) => {
        await onSubmit(values);
      }}
    >
      {(formikProps) => {
        return (
          <Box role="form" aria-label={ariaLabel}>
            {content.fields.map((field, index) => (
              <Box
                key={index}
                sx={{
                  marginBottom: theme.spacing(3),
                }}
              >
                <Field
                  key={index}
                  field={field}
                  {...formikProps}
                  isEditable={true}
                />
              </Box>
            ))}

            <DialogActions
              sx={{
                ...(canCancel && {
                  display: "flex",
                  justifyContent: "space-between",
                }),
                position: "sticky",
                bottom: 0,
                backgroundColor: theme.palette.common.white,
              }}
            >
              {canCancel && (
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  data-testid="formButton-cancel"
                  color="error"
                >
                  <TypographyI18N
                    translationKey={
                      cancelButtonLabel ?? tk("singlePageForm.cancel")
                    }
                    sx={{
                      fontWeight: theme.typography.fontWeightBold,
                    }}
                  />
                </Button>
              )}
              <Button
                variant="contained"
                data-testid="formButton-submit"
                onClick={() => {
                  formikProps.handleSubmit();
                }}
                disabled={
                  canDisableSubmitButton
                    ? !formikProps.isValid ||
                      !formikProps.dirty ||
                      disableSubmitButton
                    : false
                }
              >
                <TypographyI18N
                  translationKey={
                    confirmButtonLabel ?? tk("singlePageForm.confirm")
                  }
                  sx={{
                    color: theme.palette.primary.contrastText,
                    fontWeight: theme.typography.fontWeightBold,
                  }}
                />
              </Button>
            </DialogActions>
          </Box>
        );
      }}
    </Formik>
  );
};
